import * as React from 'react';
import { useState, useEffect, useRef } from 'react';
import { Alert, Button, Col, ControlLabel, FormControl, FormGroup, Label, Modal, Row, Well } from 'react-bootstrap';
import { IDonorRegistration } from '@nkr/models/index';
import { typeOptionList, TypeOption } from '../../../donor/add/data-model';
import { DashDonationType } from '@nkr/enums/index';
import MaskedInput from 'react-maskedinput';
import * as moment from 'moment';
import {
    cardBodyStyle,
    cardHeaderStyle,
    cardStyle,
    cardTitleTextStyle,
    fieldLabelStyle,
    fieldSurfaceStyle,
    sectionHelperStyle
} from '../styles';

const DONOR_ALIAS_REGEX = /^[a-zA-Z0-9]{6,12}$/;

const appFontStyle: React.CSSProperties = { fontFamily: 'Roboto, sans-serif' };

const intakeTypeOptions = typeOptionList.filter(x =>
    x.id === TypeOption.DonorOnlyNdd
    || x.id === TypeOption.DonorOnlyFv
    || x.id === TypeOption.DonorOnlySv
);

export interface IArPersonSearchHit {
    id: number,
    donorAlias?: string,
    recipientAlias?: string,
    donorEmail?: string,
    donorName?: string,
    donorBirthDate?: string,
    recipientFirstName?: string,
    recipientLastName?: string,
    recipientBirthDate?: string,
    surgeryDate?: string,
    watrRequestId?: number | null,
    donorRegistrationId?: number | null
}

interface ISubmittedForm {
    donorAlias: string,
    donorType?: number,
    ddRecipFirstName?: string,
    ddRecipLastName?: string,
    ddRecipDob?: string,
    recipientAlias?: string,
    linkedDonorShieldDirectId?: number | null,
    linkedDonorId?: number | null
}

/** Exclusive donor steps — never show create + search at the same time. */
type DetailsPhase = 'checking' | 'choose' | 'search' | 'matches' | 'create' | 'linked';

interface IProps {
    show: boolean,
    registration: IDonorRegistration,
    milestone?: 'workup' | 'preop',
    onClose: () => void,
    onSaveWorkupWithoutInvite: () => void,
    onSaveWithAdvancedReimbursement: (form: ISubmittedForm) => void,
    onSearchPerson?: (firstName: string, lastName: string, birthDate: string) => Promise<IArPersonSearchHit[]>,
    onSearchAliases?: (donorAlias: string, recipientAlias?: string) => Promise<IArPersonSearchHit[]>
}

const mergeHitsById = (groups: IArPersonSearchHit[][]): IArPersonSearchHit[] => {
    const byId = new Map<number, IArPersonSearchHit>();
    groups.forEach(group => {
        (group || []).forEach(hit => {
            if (hit && hit.id != null && !byId.has(hit.id)) {
                byId.set(hit.id, hit);
            }
        });
    });
    return Array.from(byId.values());
};

const toDob = (value?: string) => {
    if (!value) {
        return '';
    }
    const parsed = moment(value);
    return parsed.isValid() ? parsed.format('MM/DD/YYYY') : value;
};

const AdvancedReimbursementWorkupModal = (props: IProps) => {
    const isLiver = Number(props.registration.donationType) === DashDonationType.Liver;
    const organLabel = isLiver ? 'Liver' : 'Kidney';
    const searchPerson = props.onSearchPerson;
    const searchAliases = isLiver ? props.onSearchAliases : undefined;
    const canLookup = !!(searchPerson || searchAliases);
    const existingNoun = isLiver ? 'DonorShield Direct' : 'donor';

    const [step, setStep] = useState<'prompt' | 'details'>('prompt');
    const [detailsPhase, setDetailsPhase] = useState<DetailsPhase>('create');
    const [donorType, setDonorType] = useState<number>(TypeOption.Select);
    const [donorAlias, setDonorAlias] = useState('');
    const [recipFirstName, setRecipFirstName] = useState('');
    const [recipLastName, setRecipLastName] = useState('');
    const [recipDob, setRecipDob] = useState('');
    const [attemptedSubmit, setAttemptedSubmit] = useState(false);
    const [lookupHits, setLookupHits] = useState<IArPersonSearchHit[]>([]);
    const [selectedRecordId, setSelectedRecordId] = useState<number>(0);
    const [detailHitId, setDetailHitId] = useState<number>(0);
    const [lookupBusy, setLookupBusy] = useState(false);
    const [searchFirstName, setSearchFirstName] = useState('');
    const [searchLastName, setSearchLastName] = useState('');
    const [searchDob, setSearchDob] = useState('');
    const [searchDonorAlias, setSearchDonorAlias] = useState('');
    const [manualLookupDone, setManualLookupDone] = useState(false);
    const autoLookupStartedRef = useRef(false);

    const registrationSummary = [
        props.registration.firstName,
        props.registration.lastName,
        toDob(props.registration.birthDate)
    ].filter(x => x).join(' · ');

    const seedSearchFromRegistration = () => {
        setSearchFirstName(props.registration.firstName || '');
        setSearchLastName(props.registration.lastName || '');
        setSearchDob(toDob(props.registration.birthDate));
        setSearchDonorAlias('');
        setManualLookupDone(false);
    };

    const resetRecipientFromRegistration = () => {
        setRecipFirstName(props.registration.ddRecipFirstName || props.registration.recipFirstName || '');
        setRecipLastName(props.registration.ddRecipLastName || props.registration.recipLastName || '');
        setRecipDob(toDob(props.registration.ddRecipDob || props.registration.recipDob));
    };

    const resetFormFromRegistration = () => {
        setDonorType(TypeOption.Select);
        setDonorAlias('');
        resetRecipientFromRegistration();
        setAttemptedSubmit(false);
        setLookupHits([]);
        setSelectedRecordId(0);
        setDetailHitId(0);
        setLookupBusy(false);
        seedSearchFromRegistration();
        setDetailsPhase(canLookup ? 'checking' : 'create');
        autoLookupStartedRef.current = false;
    };

    useEffect(() => {
        if (props.show) {
            setStep('prompt');
            resetFormFromRegistration();
        }
    }, [props.show, props.registration]);

    const isPairPd = !isLiver && donorType === TypeOption.PairPd;
    const showRecipientColumn = isLiver || isPairPd;
    const selectedHit = lookupHits.find(x => x.id === selectedRecordId) || null;
    const isLinked = detailsPhase === 'linked' && selectedRecordId > 0;
    const isCreate = detailsPhase === 'create';
    const aliasSource = isLinked
        ? ((selectedHit?.donorAlias || '') || '').trim()
        : donorAlias.trim();
    const aliasValid = DONOR_ALIAS_REGEX.test(aliasSource);
    const recipDobFilled = (recipDob || '').trim() !== '';
    const recipDobValid = !recipDobFilled || moment(recipDob, 'MM/DD/YYYY', true).isValid();
    const recipNamesRequired = isLiver || isPairPd;
    const recipNamesValid = !recipNamesRequired || ((recipFirstName || '').trim() !== '' && (recipLastName || '').trim() !== '');
    const typeValid = isLiver || donorType !== TypeOption.Select;
    const detailsValid = typeValid
        && recipDobValid
        && recipNamesValid
        && (isLinked ? selectedRecordId > 0 && aliasValid : aliasValid);
    const canSave = (isCreate || isLinked) && !lookupBusy;

    const formatListLabel = (hit: IArPersonSearchHit) => {
        const name = (hit.donorName || '').trim()
            || `${hit.recipientFirstName || ''} ${hit.recipientLastName || ''}`.trim()
            || hit.donorAlias
            || `Record ${hit.id}`;
        const dob = hit.donorBirthDate || hit.recipientBirthDate || '';
        const alias = hit.donorAlias || hit.recipientAlias || '';
        return [name, dob, alias].filter(x => x).join(' · ');
    };

    const applySelectedHit = (hit: IArPersonSearchHit) => {
        setSelectedRecordId(hit.id);
        setDetailsPhase('linked');
        setAttemptedSubmit(false);
        if (hit.donorAlias) {
            setDonorAlias(hit.donorAlias);
        }
        if (hit.recipientFirstName) {
            setRecipFirstName(hit.recipientFirstName);
        }
        if (hit.recipientLastName) {
            setRecipLastName(hit.recipientLastName);
        }
        if (hit.recipientBirthDate) {
            const parsed = moment(hit.recipientBirthDate);
            setRecipDob(parsed.isValid() ? parsed.format('MM/DD/YYYY') : hit.recipientBirthDate);
        }
    };

    const personSearchReady = (searchFirstName || '').trim() !== ''
        || (searchLastName || '').trim() !== ''
        || moment(searchDob, 'MM/DD/YYYY', true).isValid();
    const aliasSearchReady = (searchDonorAlias || '').trim() !== '';
    const manualSearchReady = personSearchReady || aliasSearchReady;

    const goCreate = () => {
        setSelectedRecordId(0);
        setDetailHitId(0);
        setDonorAlias('');
        setAttemptedSubmit(false);
        resetRecipientFromRegistration();
        setDetailsPhase('create');
    };

    const goSearch = () => {
        setSelectedRecordId(0);
        setDetailHitId(0);
        setAttemptedSubmit(false);
        seedSearchFromRegistration();
        setDetailsPhase('search');
    };

    const goMatches = () => {
        setSelectedRecordId(0);
        setDetailsPhase('matches');
        setAttemptedSubmit(false);
    };

    const runManualLookup = async () => {
        if (!manualSearchReady || (!searchPerson && !searchAliases)) {
            return;
        }
        setLookupBusy(true);
        setManualLookupDone(false);
        try {
            const tasks: Promise<IArPersonSearchHit[]>[] = [];
            if (personSearchReady && searchPerson) {
                const dob = moment(searchDob, 'MM/DD/YYYY', true).isValid() ? searchDob.trim() : '';
                tasks.push(searchPerson(searchFirstName.trim(), searchLastName.trim(), dob));
            }
            if (aliasSearchReady && searchAliases) {
                tasks.push(searchAliases(searchDonorAlias.trim(), ''));
            }
            const hits = tasks.length > 0
                ? mergeHitsById(await Promise.all(tasks))
                : [];
            setLookupHits(hits);
            setSelectedRecordId(0);
            setDetailHitId(0);
            setManualLookupDone(true);
            if (hits.length > 0) {
                setDetailsPhase('matches');
            }
        } catch {
            setLookupHits([]);
            setManualLookupDone(true);
        } finally {
            setLookupBusy(false);
        }
    };

    const runAutoLookup = async () => {
        if (!canLookup) {
            setDetailsPhase('create');
            return;
        }
        setLookupBusy(true);
        setDetailsPhase('checking');
        try {
            const firstName = (props.registration.firstName || '').trim();
            const lastName = (props.registration.lastName || '').trim();
            const birthDate = toDob(props.registration.birthDate);
            const dobValid = moment(birthDate, 'MM/DD/YYYY', true).isValid();
            const tasks: Promise<IArPersonSearchHit[]>[] = [];

            if (searchPerson && (firstName || lastName || dobValid)) {
                tasks.push(searchPerson(firstName, lastName, dobValid ? birthDate : ''));
            }

            const hits = tasks.length > 0
                ? mergeHitsById(await Promise.all(tasks))
                : [];
            setLookupHits(hits);
            setSelectedRecordId(0);
            setDetailHitId(0);
            setDetailsPhase(hits.length > 0 ? 'matches' : 'choose');
        } catch {
            setLookupHits([]);
            setDetailsPhase('choose');
        } finally {
            setLookupBusy(false);
        }
    };

    useEffect(() => {
        if (!props.show || step !== 'details' || autoLookupStartedRef.current) {
            return;
        }
        autoLookupStartedRef.current = true;
        void runAutoLookup();
    }, [props.show, step]);

    const submitForm = (linkedId: number | null) => {
        const selected = lookupHits.find(x => x.id === linkedId);
        const aliasForSave = linkedId
            ? (selected?.donorAlias || '').trim()
            : donorAlias.trim();
        props.onSaveWithAdvancedReimbursement({
            donorAlias: aliasForSave,
            ...(isLiver ? {} : { donorType }),
            ...(isLiver
                ? { linkedDonorShieldDirectId: linkedId, linkedDonorId: null }
                : { linkedDonorId: linkedId }),
            ...((isLiver || isPairPd)
                ? {
                    ddRecipFirstName: recipFirstName.trim(),
                    ddRecipLastName: recipLastName.trim(),
                    ddRecipDob: recipDob.trim()
                }
                : {})
        });
    };

    const handleSaveDetails = () => {
        setAttemptedSubmit(true);
        if (!canSave || !detailsValid) {
            return;
        }
        submitForm(isLinked ? selectedRecordId : null);
    };

    const detailHit = lookupHits.find(x => x.id === detailHitId) || null;
    const milestone = props.milestone ?? 'workup';
    const isPreOp = milestone === 'preop';
    const promptTitle = step === 'prompt'
        ? (isPreOp ? 'Schedule pre-op' : 'Schedule workup')
        : 'Donor Shield Program';
    const promptContext = isPreOp
        ? 'A pre-op date has been selected.'
        : 'A workup date has been selected.';
    const saveMilestoneOnlyLabel = 'Save Without Invitation';
    const saveApplyLabel = 'Save & Send Invitation';
    const donorShieldProgramLabel = (
        <strong className='ar-ds-program-name'>Donor Shield Program</strong>
    );

    const donorStepTitle = (() => {
        switch (detailsPhase) {
            case 'checking':
                return 'Looking for an existing profile';
            case 'choose':
                return 'No match found';
            case 'search':
                return 'Search existing profile';
            case 'matches':
                return 'Confirm existing profile';
            case 'create':
                return 'Create new profile';
            case 'linked':
                return 'Using existing profile';
            default:
                return 'Donor';
        }
    })();

    const renderDonorColumn = () => (
        <div style={{ ...cardStyle, marginBottom: 0 }}>
            <div style={cardHeaderStyle}>
                <span style={cardTitleTextStyle}>{donorStepTitle}</span>
            </div>
            <div style={cardBodyStyle}>
                {!isLiver && detailsPhase !== 'checking' && (
                    <FormGroup validationState={!attemptedSubmit || typeValid ? null : 'error'} style={{ marginBottom: 16 }}>
                        <ControlLabel style={fieldLabelStyle}>Type of donor</ControlLabel>
                        <FormControl
                            componentClass='select'
                            style={fieldSurfaceStyle}
                            value={donorType}
                            onChange={(e: React.FormEvent<FormControl & HTMLSelectElement>) => setDonorType(Number(e.currentTarget.value))}
                        >
                            <option value={TypeOption.Select}>- Please Select -</option>
                            {intakeTypeOptions.map(item =>
                                <option key={`ar-intake-type-${item.id}`} value={item.id}>{item.description}</option>
                            )}
                        </FormControl>
                    </FormGroup>
                )}

                {detailsPhase === 'checking' && (
                    <p style={{ ...sectionHelperStyle, marginBottom: 0 }}>
                        Checking for an existing {existingNoun} using this registration
                        {registrationSummary ? ` (${registrationSummary})` : ''}…
                    </p>
                )}

                {detailsPhase === 'choose' && (
                    <>
                        <Alert bsStyle='info' style={{ marginBottom: 14, padding: '10px 12px' }}>
                            No existing {existingNoun} matched
                            {registrationSummary ? <> <strong>{registrationSummary}</strong></> : ' this registration'}.
                            Choose one option below.
                        </Alert>
                        <Button
                            bsStyle='primary'
                            block
                            style={{ marginBottom: 10, minHeight: 38 }}
                            onClick={goCreate}
                        >
                            Create a new {existingNoun}
                        </Button>
                        <Button
                            bsStyle='default'
                            block
                            style={{ minHeight: 38 }}
                            onClick={goSearch}
                        >
                            Search for an existing {existingNoun}
                        </Button>
                    </>
                )}

                {detailsPhase === 'search' && (
                    <>
                        <p style={{ ...sectionHelperStyle, marginBottom: 12 }}>
                            Find an existing {existingNoun} to link. This does not create a new record.
                        </p>
                        {searchAliases && (
                            <FormGroup style={{ marginBottom: 12 }}>
                                <ControlLabel style={fieldLabelStyle}>Existing donor alias</ControlLabel>
                                <FormControl
                                    style={fieldSurfaceStyle}
                                    value={searchDonorAlias}
                                    onChange={(e: React.FormEvent<FormControl & HTMLInputElement>) => setSearchDonorAlias(e.currentTarget.value)}
                                    onKeyPress={(e: React.KeyboardEvent<FormControl>) => { if (e.key === 'Enter') { e.preventDefault(); void runManualLookup(); } }}
                                    maxLength={12}
                                    placeholder='Optional'
                                />
                            </FormGroup>
                        )}
                        <FormGroup style={{ marginBottom: 12 }}>
                            <ControlLabel style={fieldLabelStyle}>First name</ControlLabel>
                            <FormControl
                                style={fieldSurfaceStyle}
                                value={searchFirstName}
                                onChange={(e: React.FormEvent<FormControl & HTMLInputElement>) => setSearchFirstName(e.currentTarget.value)}
                                onKeyPress={(e: React.KeyboardEvent<FormControl>) => { if (e.key === 'Enter') { e.preventDefault(); void runManualLookup(); } }}
                            />
                        </FormGroup>
                        <FormGroup style={{ marginBottom: 12 }}>
                            <ControlLabel style={fieldLabelStyle}>Last name</ControlLabel>
                            <FormControl
                                style={fieldSurfaceStyle}
                                value={searchLastName}
                                onChange={(e: React.FormEvent<FormControl & HTMLInputElement>) => setSearchLastName(e.currentTarget.value)}
                                onKeyPress={(e: React.KeyboardEvent<FormControl>) => { if (e.key === 'Enter') { e.preventDefault(); void runManualLookup(); } }}
                            />
                        </FormGroup>
                        <FormGroup style={{ marginBottom: 12 }}>
                            <ControlLabel style={fieldLabelStyle}>Date of birth</ControlLabel>
                            <MaskedInput
                                className='form-control'
                                style={fieldSurfaceStyle}
                                mask='11/11/1111'
                                value={searchDob}
                                onChange={(e: React.FormEvent<HTMLInputElement>) => setSearchDob(e.currentTarget.value)}
                                onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => { if (e.key === 'Enter') { e.preventDefault(); void runManualLookup(); } }}
                            />
                        </FormGroup>
                        <Button
                            bsStyle='primary'
                            disabled={lookupBusy || !manualSearchReady}
                            onClick={() => void runManualLookup()}
                            style={{ marginRight: 8 }}
                        >
                            {lookupBusy ? 'Searching…' : 'Search'}
                        </Button>
                        <Button bsStyle='link' onClick={goCreate}>
                            Create new instead
                        </Button>
                        {manualLookupDone && lookupHits.length === 0 && (
                            <Alert bsStyle='warning' style={{ marginTop: 12, marginBottom: 0, padding: '10px 12px' }}>
                                No matching records found.
                                {' '}
                                <Button bsStyle='link' bsSize='sm' style={{ padding: 0, verticalAlign: 'baseline' }} onClick={goCreate}>
                                    Create a new {existingNoun}
                                </Button>
                            </Alert>
                        )}
                    </>
                )}

                {detailsPhase === 'matches' && (
                    <>
                        <Alert bsStyle='warning' style={{ marginBottom: 12, padding: '10px 12px' }}>
                            <strong>Possible existing {existingNoun} found.</strong>
                            {' '}Select a record to link, or create a new one.
                        </Alert>
                        <div style={{ maxHeight: 180, overflowY: 'auto', border: '1px solid #ddd', borderRadius: 3, marginBottom: 12 }}>
                            {lookupHits.map(hit =>
                                <button
                                    key={`ar-lookup-row-${hit.id}`}
                                    type='button'
                                    onClick={() => setDetailHitId(hit.id)}
                                    style={{
                                        display: 'block',
                                        width: '100%',
                                        textAlign: 'left',
                                        border: 'none',
                                        borderBottom: '1px solid #eee',
                                        padding: '8px 10px',
                                        background: detailHitId === hit.id ? '#eaf2fa' : '#fff',
                                        cursor: 'pointer'
                                    }}
                                >
                                    {formatListLabel(hit)}
                                </button>
                            )}
                        </div>
                        {detailHit && (
                            <Well bsSize='sm' style={{ marginBottom: 12, background: '#fafafa' }}>
                                <div style={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, marginBottom: 6 }}>Selected record</div>
                                <div><strong>Donor:</strong> {detailHit.donorName || '—'} · Alias {detailHit.donorAlias || '—'} · DOB {detailHit.donorBirthDate || '—'}</div>
                                <div><strong>Email:</strong> {detailHit.donorEmail || '—'}</div>
                                <div><strong>WATR:</strong> {detailHit.watrRequestId || 'None'}</div>
                                <div style={{ marginTop: 10 }}>
                                    <Button bsStyle='primary' bsSize='sm' onClick={() => applySelectedHit(detailHit)}>
                                        Use this existing record
                                    </Button>
                                </div>
                            </Well>
                        )}
                        <Button bsStyle='default' bsSize='sm' style={{ marginRight: 8 }} onClick={goCreate}>
                            Create new instead
                        </Button>
                        <Button bsStyle='link' bsSize='sm' style={{ paddingLeft: 0 }} onClick={goSearch}>
                            Search again
                        </Button>
                    </>
                )}

                {detailsPhase === 'linked' && selectedHit && (
                    <>
                        <Alert bsStyle='success' style={{ marginBottom: 12, padding: '10px 12px' }}>
                            Linking to existing {existingNoun}:
                            {' '}
                            <strong>{formatListLabel(selectedHit)}</strong>
                        </Alert>
                        <p style={{ ...sectionHelperStyle, marginBottom: 8 }}>
                            Existing alias <strong>{selectedHit.donorAlias || '—'}</strong> will be used. No new alias is created.
                        </p>
                        <Button bsStyle='link' bsSize='sm' style={{ paddingLeft: 0 }} onClick={goMatches}>
                            Choose a different record
                        </Button>
                    </>
                )}

                {detailsPhase === 'create' && (
                    <>
                        <Alert bsStyle='info' style={{ marginBottom: 12, padding: '10px 12px' }}>
                            <strong>You are creating a new {existingNoun}.</strong>
                            {' '}Enter a new donor alias for this registration.
                        </Alert>
                        <FormGroup validationState={!attemptedSubmit || aliasValid ? null : 'error'} style={{ marginBottom: 8 }}>
                            <ControlLabel style={fieldLabelStyle}>New donor alias</ControlLabel>
                            <FormControl
                                style={fieldSurfaceStyle}
                                value={donorAlias}
                                onChange={(e: React.FormEvent<FormControl & HTMLInputElement>) => setDonorAlias(e.currentTarget.value)}
                                minLength={6}
                                maxLength={12}
                                placeholder='6–12 letters or numbers'
                            />
                            {!aliasValid && attemptedSubmit
                                ? <span className='help-block'>Letters and numbers only; 6 to 12 characters.</span>
                                : <span className='help-block' style={{ marginBottom: 0 }}>This alias is for the new profile only.</span>}
                        </FormGroup>
                        {canLookup && (
                            <Button bsStyle='link' bsSize='sm' style={{ paddingLeft: 0, marginTop: 4 }} onClick={goSearch}>
                                Search for an existing profile instead
                            </Button>
                        )}
                        {canLookup && lookupHits.length > 0 && (
                            <div>
                                <Button bsStyle='link' bsSize='sm' style={{ paddingLeft: 0 }} onClick={goMatches}>
                                    Review previous search results
                                </Button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );

    const renderRecipientColumn = () => (
        <div style={{ ...cardStyle, marginBottom: 0 }}>
            <div style={cardHeaderStyle}>
                <span style={cardTitleTextStyle}>Recipient</span>
            </div>
            <div style={cardBodyStyle}>
                {isLiver && (
                    <p style={{ ...sectionHelperStyle, marginBottom: 12 }}>
                        Directed donor — details from registration when available. Date of birth is optional.
                    </p>
                )}
                <FormGroup validationState={!attemptedSubmit || (recipFirstName || '').trim() !== '' ? null : 'error'} style={{ marginBottom: 16 }}>
                    <ControlLabel style={fieldLabelStyle}>First name</ControlLabel>
                    <FormControl
                        style={fieldSurfaceStyle}
                        value={recipFirstName}
                        onChange={(e: React.FormEvent<FormControl & HTMLInputElement>) => setRecipFirstName(e.currentTarget.value)}
                    />
                </FormGroup>
                <FormGroup validationState={!attemptedSubmit || (recipLastName || '').trim() !== '' ? null : 'error'} style={{ marginBottom: 16 }}>
                    <ControlLabel style={fieldLabelStyle}>Last name</ControlLabel>
                    <FormControl
                        style={fieldSurfaceStyle}
                        value={recipLastName}
                        onChange={(e: React.FormEvent<FormControl & HTMLInputElement>) => setRecipLastName(e.currentTarget.value)}
                    />
                </FormGroup>
                <FormGroup validationState={!attemptedSubmit || recipDobValid ? null : 'error'} style={{ marginBottom: 8 }}>
                    <ControlLabel style={fieldLabelStyle}>
                        Date of birth{isLiver ? ' (optional)' : ''}
                    </ControlLabel>
                    <MaskedInput
                        className='form-control'
                        style={fieldSurfaceStyle}
                        mask='11/11/1111'
                        value={recipDob}
                        onChange={(e: React.FormEvent<HTMLInputElement>) => setRecipDob(e.currentTarget.value)}
                    />
                </FormGroup>
            </div>
        </div>
    );

    return (
        <Modal
            backdrop='static'
            show={props.show}
            onHide={props.onClose}
            bsSize={step === 'details' && showRecipientColumn ? 'large' : undefined}
            className='modal-vertical-center'
            dialogClassName={step === 'prompt' ? 'ar-advanced-reimbursement-modal ar-ds-invite-prompt-modal' : 'ar-advanced-reimbursement-modal'}
            style={{ display: 'flex', alignItems: 'center', ...appFontStyle }}
        >
            <Modal.Header closeButton style={appFontStyle}>
                <Modal.Title style={{ ...appFontStyle, fontWeight: 500, whiteSpace: step === 'details' ? 'nowrap' : undefined }}>
                    {step === 'details' ? donorShieldProgramLabel : promptTitle}
                    {step === 'details' && (
                        <Label bsStyle='default' style={{ marginLeft: 10, verticalAlign: 'middle', fontWeight: 500, fontFamily: 'Roboto, sans-serif' }}>
                            {organLabel}
                        </Label>
                    )}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body style={appFontStyle} className={step === 'prompt' ? 'ar-ds-invite-prompt-body' : undefined}>
                {step === 'prompt'
                    ? (
                        <div className='ar-ds-invite-prompt'>
                            <p className='ar-ds-invite-prompt__context'>{promptContext}</p>
                            <p className='ar-ds-invite-prompt__question'>
                                Would you like to invite this donor to the {donorShieldProgramLabel}?
                            </p>
                        </div>
                    )
                    : (
                        <Row>
                            <Col xs={12} md={showRecipientColumn ? 6 : 12} style={{ marginBottom: showRecipientColumn ? 16 : 0 }}>
                                {renderDonorColumn()}
                            </Col>
                            {showRecipientColumn && (
                                <Col xs={12} md={6} style={{ marginBottom: 0 }}>
                                    {renderRecipientColumn()}
                                </Col>
                            )}
                        </Row>
                    )}
            </Modal.Body>
            <Modal.Footer
                style={appFontStyle}
                className={step === 'prompt' ? 'ar-ds-invite-prompt-footer' : undefined}
            >
                {step === 'prompt'
                    ? (
                        <>
                            <Button onClick={() => props.onClose()}>
                                Cancel
                            </Button>
                            <div className='ar-ds-invite-prompt-footer__actions'>
                                <Button
                                    bsStyle='default'
                                    onClick={() => { props.onSaveWorkupWithoutInvite(); }}
                                >
                                    {saveMilestoneOnlyLabel}
                                </Button>
                                <Button
                                    bsStyle='primary'
                                    onClick={() => {
                                        resetFormFromRegistration();
                                        setStep('details');
                                    }}
                                >
                                    {saveApplyLabel}
                                </Button>
                            </div>
                        </>
                    )
                    : (
                        <>
                            <Button className='pull-left' onClick={() => props.onClose()}>
                                Cancel
                            </Button>
                            <div className='pull-right'>
                                <Button
                                    bsStyle='default'
                                    style={{ marginLeft: 8, minHeight: 34 }}
                                    onClick={() => { setStep('prompt'); setAttemptedSubmit(false); }}
                                >
                                    Back
                                </Button>
                                <Button
                                    bsStyle='primary'
                                    style={{ marginLeft: 8, minHeight: 34 }}
                                    disabled={!canSave}
                                    onClick={() => handleSaveDetails()}
                                >
                                    {saveApplyLabel}
                                </Button>
                            </div>
                            <div className='clearfix' />
                        </>
                    )}
            </Modal.Footer>
        </Modal>
    );
};

export default AdvancedReimbursementWorkupModal;
export type { ISubmittedForm as IAdvancedReimbursementSubmittedForm };
